import { useCallback } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { ExportIcon } from '@icons/ExportIcon';
import { Button } from '@components/Button';
import { EpisodeNamedEntity } from '@insights_module/components/episode_details_screen/EpisodeNamedEntity';
import { useEpisodeNamedEntities } from '@insights_module/hooks/useEpisodeNamedEntities';
import { LinkedNamedEntityForEpisodeItem } from '@insights_module/types/ContextualAndEntity';
import { SidebarItem } from '@insights_module/components/Sidebar';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { download } from '@utils/download';
import { useEpisodeLinkedNamedEntities } from '@insights_module/hooks/useEpisodeLinkedNamedEntities';

const Separator = tw.div`w-full h-[1px] bg-gray-1 mb-[4px]`;
const Row = tw.div`flex items-center justify-between text-[11px] text-text-and-icon-muted p-[6px] rounded-[4px]`;
const HeadCell = tw.div`font-semibold text-text-and-icon-muted-2 text-[12px]`;
const Fallback = tw.div`bg-selected py-4 px-3 rounded-md text-text-and-icon-muted text-[13px] box-border mt-2 ml-[-4px] mr-1`;
const Title = tw.span`text-left text-[14px] font-semibold text-text-and-icon-muted tracking-[0.14px] px-[8px]`;
const FlexStart = tw.div`flex items-center justify-start gap-[6px]`;
const StyledButton = tw(
  Button.Primary
)`w-full flex items-center justify-between py-[10px] px-[12px] h-auto [&_svg]:fill-current [&_svg]:w-4 [&_svg]:h-4 [&_svg]:transition-all [&_svg]:duration-200`;

export const EpisodeEntitiesSection = () => {
  const { t } = useTranslation();
  const { linkedNamedEntities, isLoading } = useEpisodeLinkedNamedEntities();

  return (
    <SidebarItem isLoading={isLoading} header={null}>
      <Title>
        {t('insights.episode_details_drawer.episode_details_sidebar.entities.table.title')}
      </Title>
      <div>
        <Row tw='p-[8px]'>
          <HeadCell>
            {t('insights.episode_details_drawer.episode_details_sidebar.entities.table.head_1')}
          </HeadCell>
          <HeadCell>
            {t('insights.episode_details_drawer.episode_details_sidebar.entities.table.head_2')}
          </HeadCell>
          <div />
        </Row>
        <Separator />
        <div>
          {!isLoading && !linkedNamedEntities.length && (
            <Fallback>
              {t('insights.episode_details_drawer.episode_details_sidebar.entities.table.no_data')}
            </Fallback>
          )}
          {linkedNamedEntities?.map(
            (linkedNamedEntity: LinkedNamedEntityForEpisodeItem, index: number) => (
              <EpisodeNamedEntity key={index} entity={linkedNamedEntity} />
            )
          )}
        </div>
      </div>
    </SidebarItem>
  );
};

export const DownloadEntitiesButton = () => {
  const { t } = useTranslation();
  const { namedEntities } = useEpisodeNamedEntities();
  const { selectedEpisode } = useInsightsContext();

  const handleDownloadClick = useCallback(() => {
    const file = new Blob(
      [namedEntities.map((item) => `${item.value} - [${item.type}]`).join('\n')],
      {
        type: 'text/plain',
      }
    );
    const fileName = `named-entities-${selectedEpisode}.txt`;

    download(file, fileName);
  }, [namedEntities]);

  return (
    <StyledButton onClick={handleDownloadClick}>
      <FlexStart>
        <ExportIcon />
        {t('insights.episode_details_drawer.episode_details_sidebar.entities.download_button')}
      </FlexStart>
    </StyledButton>
  );
};
